import { z } from 'zod';

import { commerceCapabilitySchema } from './commerce.schema';
import { idSchema, isoDateSchema, jsonMetaSchema } from './common.schema';

export const dataConnectorAuthSchema = z.object({
  /**
   * Откуда берётся доступ к внешней системе:
   *  - bearer / basic / api_key — секрет введён руками и лежит зашифрованным здесь;
   *  - partner_link — секрета здесь нет, доступ берётся из связки с
   *    платформой (`link_id` → partner_links): OAuth-токен, который сам
   *    обновляется, или ключ, выданный при сопряжении кодом. Включается,
   *    когда продавец связал аккаунты — вводить ничего не нужно.
   */
  type: z.enum(['bearer', 'basic', 'api_key', 'partner_link']),
  base_url: z.string().url(),
  /** Имя заголовка/параметра для api_key (по умолчанию `Authorization`) */
  token_field: z.string().optional(),
  username: z.string().optional(),
  /** Только для type='partner_link': связка, которая владеет доступом */
  link_id: idSchema.optional(),
  /** Сам токен передаётся отдельно при create/update — в БД хранится зашифрованным */
});

export const dataConnectorSchema = z.object({
  id: idSchema,
  workspace_id: idSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  kind: z.string(),
  auth: dataConnectorAuthSchema,
  /**
   * Возможность → строка запроса: `"search_order": "GET /orders?q={{query}}"`.
   * Ключи — из COMMERCE_CAPABILITIES; чего нет в карте, того коннектор не умеет,
   * и панель оператора этот блок не показывает.
   */
  endpoints: z.record(commerceCapabilitySchema, z.string()),
  /**
   * Приведение ответа к каноническим формам (@see commerce.schema).
   * Ключ — путь в нашей форме (`order.status`), значение — путь во внешнем
   * ответе (`fulfillment.state`). Поддерживаются `a.b.c` и `items[].title`.
   */
  field_mapping: z.record(z.string()),
  display: jsonMetaSchema,
  quick_actions: z.array(jsonMetaSchema),
  cache_ttl_sec: z.number().int().nonnegative(),
  rate_limit_per_min: z.number().int().nonnegative(),
  timeout_ms: z.number().int().nonnegative(),
  allow_mutations: z.boolean(),
  is_active: z.boolean(),
  created_at: isoDateSchema,
  updated_at: isoDateSchema,
});
export type DataConnector = z.infer<typeof dataConnectorSchema>;

export const createDataConnectorSchema = z.object({
  name: z.string().min(1).max(200),
  /** Магазин, чьи заказы и товары отдаёт источник; пусто — общий */
  store_id: idSchema.nullable().optional(),
  /** Идентификатор пресета: salesaas / bitrix / woocommerce / custom */
  kind: z.string().max(64).default('custom'),
  description: z.string().optional(),
  auth: dataConnectorAuthSchema,
  /** Plaintext token / password — перед записью в БД будет шифроваться */
  secret: z.string().min(1).max(2048).optional(),
  endpoints: z.record(commerceCapabilitySchema, z.string()),
  field_mapping: z.record(z.string()).optional(),
  display: jsonMetaSchema.optional(),
  quick_actions: z.array(jsonMetaSchema).optional(),
  cache_ttl_sec: z.number().int().nonnegative().default(60),
  rate_limit_per_min: z.number().int().nonnegative().default(100),
  timeout_ms: z.number().int().min(100).max(10000).default(3000),
  allow_mutations: z.boolean().default(false),
});

export const dataConnectorQuerySchema = z.object({
  /** Какая возможность вызывается */
  type: commerceCapabilitySchema,
  params: z.record(z.unknown()).default({}),
  /**
   * Тикет, из карточки которого пришёл запрос. Для данных о покупателях и
   * заказах обязателен оператору: по нему проверяется, что у него есть
   * доступ к этой переписке, — иначе через поиск заказов можно было бы
   * смотреть покупателей из источников, закрытых ему настройками.
   */
  ticket_id: idSchema.optional(),
  /** Обойти кэш (кнопка «обновить» в панели) */
  fresh: z.boolean().default(false),
});

/**
 * Автоподгрузка при открытии тикета: сервер сам ищет покупателя по контактам
 * тикета и, если нашёл, сразу тянет его заказы. Отдельная схема, потому что
 * параметров у неё нет — всё берётся из контакта.
 */
export const dataConnectorContextQuerySchema = z.object({
  ticket_id: idSchema,
  fresh: z.boolean().default(false),
});

/**
 * Оператор подтвердил или выбрал покупателя для собеседника. Покупатель
 * передаётся целиком (как пришёл из поиска), чтобы панель могла показать
 * его, не дожидаясь внешней системы.
 */
export const commerceCustomerLinkSchema = z.object({
  ticket_id: idSchema,
  customer: z.object({
    external_id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    orders_count: z.number().optional(),
  }),
});
